/**
 * `MessageResponseSchema` — public read-API response shape for a message
 * Mirrors the `MessageEnvelopeSchema` discriminated union (text / image /
 * audio / video / document / location) so the authenticated agent UI can
 * render full message content directly from the response payload without
 * re-deriving the discriminator from server internals.
 *
 * INCLUDED — `payload`: the discriminated `payload` variant is exposed
 * in the authenticated agent-facing API response. PII must stay out of
 * observability data such as logs, OTel attributes/spans, and worker
 * persistence projections; that restriction does not apply to this
 * authenticated read API, whose consumers need message content.
 *
 * OMITTED fields (each documented inline below):
 *   - `envelope`         — wire-format wrapper; not a storage/response field
 *   - `_id`              — sha256 dedup key, server-internal
 *   - `_schemaVersion`   — server-only storage version
 *   - `status`           — outbound outbox metadata
 *   - `dispatchedAt`     — outbound outbox metadata
 *
 * Consumers: the conversation service `api/` layer maps `MessageDocument`
 * to this schema before responding. `apps/clients/web` imports it from
 * `@voll-intelligence/domain` for client-side parsing.
 */
import { z } from 'zod';

import { ChatChannelSchema } from '../card/card';
import { LanguageCodeSchema } from '../_shared/language';
import { ContactRefSchema } from './contact_ref';
import { MessageAuthorSchema } from './message_author';
import {
  AttachmentSchema,
  ConversationIdSchema,
  MessageDirectionSchema,
  MessageIdSchema,
} from './message_envelope';
import { EnvelopeOrgIdSchema } from './org_id';

export const TranslationSchema = z
  .object({
    targetLang: LanguageCodeSchema,
    text: z.string().min(1).max(10_000),
    engine: z.enum(['amazon-translate', 'operator-original']),
  })
  .strict();
export type Translation = z.infer<typeof TranslationSchema>;

/**
 * Common response fields shared across every messageType variant. The
 * shape mirrors `EnvelopeBaseShape` from `message_envelope.ts` MINUS the
 * `envelope` wire wrapper, PLUS `conversationId` (persisted on
 * `MessageDocument` and required by read-API consumers — not present in
 * `EnvelopeBaseShape`). `timestamp` is `z.coerce.date()` so the schema
 * accepts both a `Date` object (backend, from already-parsed documents) and
 * an ISO string (web client, wire JSON from HTTP responses and SSE frames).
 */
const MessageResponseBaseShape = {
  messageId: MessageIdSchema,
  externalMessageId: z.string().min(1).max(200),
  channel: ChatChannelSchema,
  contactRef: ContactRefSchema,
  orgId: EnvelopeOrgIdSchema,
  conversationId: ConversationIdSchema,
  timestamp: z.coerce.date(),
  direction: MessageDirectionSchema,
  author: MessageAuthorSchema,
  attachments: z.array(AttachmentSchema).max(20).default([]),
  channelMetadata: z.record(z.string(), z.unknown()).default({}),
  // Derived PT rendition for the operator (display) OR the operator's verbatim
  // original (engine='operator-original'). Absent when no translation applies.
  translation: TranslationSchema.optional(),
} as const;

export const TextMessageResponseSchema = z
  .object({
    ...MessageResponseBaseShape,
    messageType: z.literal('text'),
    // `ui` — opaque generative-UI block (ChatUi) carried on agent text replies so a
    // reloaded web-chat conversation re-renders the card. Persisted on the message
    // doc's `payload` bag and projected through verbatim. Kept opaque (`z.unknown`)
    // and validated against ChatUiSchema at the app-integration boundary (mirrors the
    // live SSE path) — so a contract drift degrades to a text-only frame instead of
    // dropping the whole message on read. Absent on plain-text replies.
    //
    // `body` may be EMPTY only when `ui` is present: a domain agent (flight/rental
    // search, etc.) legitimately answers with a card and no prose — forcing a
    // non-empty body there would make the shell invent filler text. Empty body
    // WITHOUT a `ui` block stays rejected — that combination is the canonical
    // offender this constraint exists for: an `AgentsReply` with
    // `status:'handoff_to_human'`/`'error'` persists `response:null` as an empty
    // payload with no `ui`, which must still be dropped on read (`to_message_response.ts`).
    // `reasoning` — registro do raciocínio do turno (`AppChatReasoning`), pelo
    // mesmo trilho opaco do `ui`: persistido no bag do payload e projetado
    // verbatim, validado na borda do app-integration. Presente só em resposta do
    // agente; ausente em todo histórico anterior ao campo. **Tem que estar
    // declarado aqui**: este payload é `.strict()`, e chave não declarada dropa a
    // MENSAGEM INTEIRA na leitura (`projectMessages` é fail-open por documento).
    payload: z
      .object({
        body: z.string().max(10_000),
        ui: z.unknown().optional(),
        reasoning: z.unknown().optional(),
        // Zendesk desk: `deskAgent` (who answered, `DeskAgentProfile`) and
        // `deskSession` (start/end divider, `DeskSessionMarker`) ride the same
        // opaque rail, validated at the app-integration border. **Declare them
        // here**: the payload is `.strict()` and an undeclared key drops the
        // whole message on read in `apps/clients/web`.
        deskAgent: z.unknown().optional(),
        deskSession: z.unknown().optional(),
      })
      .strict()
      .refine((p) => p.body.length > 0 || (p.ui !== undefined && p.ui !== null), {
        message: 'body must be non-empty unless a ui block is present',
        path: ['body'],
      }),
  })
  .strict();
export type TextMessageResponse = z.infer<typeof TextMessageResponseSchema>;

export const ImageMessageResponseSchema = z
  .object({
    ...MessageResponseBaseShape,
    messageType: z.literal('image'),
    payload: z
      .object({
        url: z.url(),
        mimeType: z.string().max(100).optional(),
        caption: z.string().max(1024).optional(),
      })
      .strict(),
  })
  .strict();
export type ImageMessageResponse = z.infer<typeof ImageMessageResponseSchema>;

export const AudioMessageResponseSchema = z
  .object({
    ...MessageResponseBaseShape,
    messageType: z.literal('audio'),
    payload: z
      .object({
        url: z.url(),
        mimeType: z.string().max(100).optional(),
        caption: z.string().max(1024).optional(),
        // audio-transcription: when the inbound audio was transcribed, the read
        // API projects the transcript as `body` so consumers that already read
        // `payload.body` (web card, kanban-api) get uniform text across message
        // types — without re-deriving from the discriminator. `transcribed`
        // flags that this `body` is a machine transcript, NOT a literally typed
        // text body, so consumers can distinguish the two (audit / UX). Both are
        // optional + additive: audio docs without a transcript omit them.
        body: z.string().max(10_000).optional(),
        transcribed: z.boolean().optional(),
      })
      .strict(),
  })
  .strict();
export type AudioMessageResponse = z.infer<typeof AudioMessageResponseSchema>;

export const VideoMessageResponseSchema = z
  .object({
    ...MessageResponseBaseShape,
    messageType: z.literal('video'),
    payload: z
      .object({
        url: z.url(),
        mimeType: z.string().max(100).optional(),
        caption: z.string().max(1024).optional(),
      })
      .strict(),
  })
  .strict();
export type VideoMessageResponse = z.infer<typeof VideoMessageResponseSchema>;

export const DocumentMessageResponseSchema = z
  .object({
    ...MessageResponseBaseShape,
    messageType: z.literal('document'),
    payload: z
      .object({
        url: z.url(),
        mimeType: z.string().max(100).optional(),
        filename: z.string().min(1).max(200),
        caption: z.string().max(1024).optional(),
      })
      .strict(),
  })
  .strict();
export type DocumentMessageResponse = z.infer<typeof DocumentMessageResponseSchema>;

export const LocationMessageResponseSchema = z
  .object({
    ...MessageResponseBaseShape,
    messageType: z.literal('location'),
    payload: z
      .object({
        latitude: z.number().min(-90).max(90),
        longitude: z.number().min(-180).max(180),
        name: z.string().max(200).optional(),
        address: z.string().max(500).optional(),
      })
      .strict(),
  })
  .strict();
export type LocationMessageResponse = z.infer<typeof LocationMessageResponseSchema>;

export const MessageResponseSchema = z.discriminatedUnion('messageType', [
  TextMessageResponseSchema,
  ImageMessageResponseSchema,
  AudioMessageResponseSchema,
  VideoMessageResponseSchema,
  DocumentMessageResponseSchema,
  LocationMessageResponseSchema,
]);

export type MessageResponse = z.infer<typeof MessageResponseSchema>;
